import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.04, duration: 0.4 } }) };
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const statusColors = {
  available: "bg-green-500/10 text-green-400 border-green-500/20 hover:bg-green-500/20",
  limited: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  fully_booked: "bg-red-500/10 text-red-400/60 border-red-500/20",
  event: "bg-gold/15 text-gold border-gold/30",
};

export default function CalendarPage() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/calendar/availability?month=${month}&year=${year}`).then(r => setDays(r.data.days)).catch(() => {}).finally(() => setLoading(false));
  }, [month, year]);

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const firstDayOfWeek = new Date(year, month - 1, 1).getDay();
  const blanks = Array.from({ length: firstDayOfWeek }, (_, i) => i);

  return (
    <div data-testid="calendar-page" className="pt-20 min-h-screen bg-luxury-black">
      <section className="py-24 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" animate="visible" className="mb-12 text-center">
            <motion.p variants={fadeUp} custom={0} className="text-xs font-body font-medium uppercase tracking-[0.3em] text-gold mb-4">Availability</motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="font-heading text-4xl sm:text-5xl font-light text-white mb-4">
              Booking <span className="gold-gradient-text">Calendar</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-white/50 font-body text-sm max-w-md mx-auto">
              Check our availability and find the perfect date for your event. Green dates are open, gold dates have events you can attend.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={3} className="glass-card p-8">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-8">
              <button onClick={prevMonth} data-testid="calendar-prev" className="text-white/40 hover:text-gold transition-colors p-2"><ChevronLeft size={20} /></button>
              <h2 className="font-heading text-2xl text-white" data-testid="calendar-month-year">{MONTHS[month - 1]} {year}</h2>
              <button onClick={nextMonth} data-testid="calendar-next" className="text-white/40 hover:text-gold transition-colors p-2"><ChevronRight size={20} /></button>
            </div>

            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-2 mb-2">
              {WEEKDAYS.map(d => <div key={d} className="text-center text-white/20 text-[10px] font-body uppercase tracking-wider py-2">{d}</div>)}
            </div>

            {/* Days Grid */}
            {loading ? <div className="text-gold animate-pulse text-sm font-body text-center py-16">Loading...</div> : (
              <div className="grid grid-cols-7 gap-2">
                {blanks.map(i => <div key={`blank-${i}`} />)}
                {days.map(d => (
                  <button
                    key={d.date}
                    data-testid={`calendar-day-${d.day}`}
                    onClick={() => setSelectedDay(selectedDay?.date === d.date ? null : d)}
                    className={`aspect-square flex flex-col items-center justify-center border rounded-sm text-sm font-body transition-all ${statusColors[d.status] || statusColors.available} ${selectedDay?.date === d.date ? "ring-1 ring-gold scale-105" : ""}`}
                  >
                    <span className="font-medium">{d.day}</span>
                    {d.event && <span className="text-[8px] text-gold/60 truncate max-w-full px-1">Event</span>}
                    {d.status === "limited" && <span className="text-[8px]">Limited</span>}
                  </button>
                ))}
              </div>
            )}

            {/* Legend */}
            <div className="flex items-center justify-center gap-6 mt-8 text-xs font-body text-white/40">
              <span className="flex items-center gap-2"><span className="w-3 h-3 bg-green-500/20 border border-green-500/30 rounded-sm" /> Available</span>
              <span className="flex items-center gap-2"><span className="w-3 h-3 bg-yellow-500/20 border border-yellow-500/30 rounded-sm" /> Limited</span>
              <span className="flex items-center gap-2"><span className="w-3 h-3 bg-red-500/20 border border-red-500/30 rounded-sm" /> Fully Booked</span>
              <span className="flex items-center gap-2"><span className="w-3 h-3 bg-gold/20 border border-gold/30 rounded-sm" /> Event Day</span>
            </div>

            {/* Selected Day Detail */}
            {selectedDay && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 p-5 border border-white/10 bg-white/[0.02]" data-testid="selected-day-detail">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-body text-sm font-medium">{selectedDay.date}</p>
                    <p className="text-white/40 text-xs font-body mt-1">
                      {selectedDay.status === "available" && "This date is available for booking!"}
                      {selectedDay.status === "limited" && `${selectedDay.bookings} booking(s) — limited availability`}
                      {selectedDay.status === "fully_booked" && "This date is fully booked."}
                      {selectedDay.status === "event" && `Event: ${selectedDay.event}`}
                    </p>
                  </div>
                  {selectedDay.status === "available" && (
                    <Link to="/booking" data-testid="book-from-calendar" className="flex items-center gap-2 bg-gold text-black font-body text-xs font-medium px-5 py-2 hover:bg-gold-light transition-colors">
                      Book This Date <ArrowRight size={14} />
                    </Link>
                  )}
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
